import React, { useState } from 'react';
import SanitizedMarkdown from 'design-system/SanitizedMarkdown';
import { Button } from 'design-system/Button';
import { Switch } from 'design-system/Switch';
import apiFetch, { defaultApiFetchErrorHandler } from '@scale/frontend-lib/api-fetch';
import TextInput from 'design-system/TextInput';
import { requiredAsterisk as defaultAsterisk } from 'react-scale/corp/helpers';
import { makeStyles } from 'design-system/styles';

const description = `
### Description:
- This tool allows you to trigger a job that will backfill attempts for ESCI tasks
- Select the spreadsheet ID and sheet name
- Make sure your sheet has the following columns:
- Click on Upload button

##### Author: fabian.marin@scale.com
---
`;

const defaultSpreadSheetId = '1-iwbIXqsAiHT-nI20K1DJTe2IkuHCtwnbl7deU6WO8I';

const useStyles = makeStyles({
  backfillESCIAttempts: {
    display: 'flex',
    flexDirection: 'column',
    '&__subsectionTitle': {
      display: 'flex',
      flexDirection: 'row',
      '&Element': {
        '&:not(:last-child)': {
          marginRight: '.5rem',
        },
      },
    },
    '&__subsection': {
      '&:not(:last-child)': {
        marginBottom: '2rem',
      },
    },
  },
});

// eslint-disable-next-line complexity
const BackfillESCIAttempts = () => {
  const [spreadSheetId, setSpreadSheetId] = useState<string>(defaultSpreadSheetId);
  const [sheet, setSheet] = useState<string>('');
  const [inputError, setInputError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<string>();
  const [shouldDryRun, setShouldDryRun] = useState(false);

  const classes = useStyles();

  const enabled = !!spreadSheetId && !!sheet;

  const handleUpload = async () => {
    setLoading(true);
    setMessage('');

    try {
      const res = await apiFetch(null, 'corp-api/jobs/backfillImbeEsciAttempts', {
        method: 'POST',
        body: {
          input: JSON.stringify({ spreadSheetId, sheetName: sheet, dryRun: shouldDryRun }),
          metadata: {
            reason: `backfilling attempts for spreadsheet: ${spreadSheetId}, sheet: ${sheet}`,
          },
          isAsync: true,
        },
      });

      setSpreadSheetId(defaultSpreadSheetId);
      setSheet('');
      setLoading(false);
      setInputError('');

      const executionName = (res.metadata?.stepFunctionArn || '').split(':').pop();
      const message = `Successfully ran corp-job with execution-name: ${executionName}`;

      setMessage(message);
    } catch (error) {
      defaultApiFetchErrorHandler(error, { handleHttp: true });
      setLoading(false);
    }
  };

  const buttonText = () => {
    if (!spreadSheetId || !!inputError) return 'Input spreadsheet URL';
    if (!sheet) return 'Input sheet name';
    return 'Backfill ESCI Attempts';
  };

  const renderOutput = () => {
    return (
      <div className="response-markdown">
        <SanitizedMarkdown text={message || ''} />
      </div>
    );
  };

  const onChangeSpreadsheetURL = (
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    setSpreadSheetId(event.target.value.trim());
  };

  const onChangeSheet = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    setSheet(event.target.value.trim());
  };

  function handleClickDryRun(e: React.ChangeEvent<HTMLInputElement>) {
    setShouldDryRun(e.target.checked);
  }

  return (
    <div className={classes.backfillESCIAttempts}>
      <section>
        <SanitizedMarkdown text={description} />
      </section>
      <section className={`container ${classes.backfillESCIAttempts}__subsection`}>
        <div className={`${classes.backfillESCIAttempts}__subsectionTitle`}>
          <div className={`medium-text ${classes.backfillESCIAttempts}__subsectionTitleElement`}>
            Spreadsheet URL
          </div>{' '}
          {defaultAsterisk}
        </div>
        <div className="file-validation">
          <div className="input-component">
            <TextInput value={spreadSheetId} onChange={onChangeSpreadsheetURL} fullWidth />
            <small className="hint">
              Please make sure you shared the sheet with
              `imbe-google-drive@scaleapi.iam.gserviceaccount.com`
            </small>
          </div>
        </div>
      </section>
      <section
        className={
          spreadSheetId
            ? `container ${classes.backfillESCIAttempts}__subsection`
            : `container disabled ${classes.backfillESCIAttempts}__subsection`
        }
      >
        <div className={`${classes.backfillESCIAttempts}__subsectionTitle`}>
          <div className={`medium-text ${classes.backfillESCIAttempts}__subsectionTitleElement`}>
            Sheet Name
          </div>{' '}
          {defaultAsterisk}
        </div>
        <div className="file-validation">
          <div className="input-component">
            <TextInput value={sheet} onChange={onChangeSheet} disabled={!spreadSheetId} fullWidth />
          </div>
        </div>
      </section>
      <section className={`${classes.backfillESCIAttempts}__subsection`}>
        <div>Do a dry run</div>
        <Switch checked={shouldDryRun} onChange={handleClickDryRun} />
      </section>
      <section className={`${classes.backfillESCIAttempts}__subsection`}>
        <Button
          type="primary"
          disabled={!enabled || !spreadSheetId || !!inputError || !sheet || loading}
          onClick={handleUpload}
        >
          {buttonText()}
        </Button>
        {message && renderOutput()}
      </section>
    </div>
  );
};

export default BackfillESCIAttempts;
